








































import { mapActions, mapState, mapGetters } from 'vuex';
import { DateTime } from 'luxon';
import { roundNumber } from '@/helpers/roundNumber';
import VExtendedDataPicker from '@/components/VExtendedDataPicker.vue';
import {
  AvgNgrStatsItem,
  AvgUsersStatsItem,
  SelectItem,
  ParamsGraphRequest
} from '@/api/schema';
import {
  getHalfYearPeriodDates,
  getMonthPeriodDates
} from '@/helpers/getPeriodDates';
import VueApexCharts from 'vue-apexcharts';
import { isAllowedPreviousDate } from '@/helpers/allowedDatepickersDates';
import { ELoyaltyFeatureFlag } from '@/store/modules/feature-flags/FeatureFlagNames';

const EChartsTypeKey = {
  USERS: 'USERS',
  NGR: 'NGR_PER_USER'
};

interface IChartConfigItem {
  key: keyof typeof EChartsTypeKey;
  data: AvgNgrStatsItem[] | AvgUsersStatsItem[];
  series: unknown[];
  chartType: string;
  colors: string[];
  isShowDatePicker?: boolean;
  isShowDescription?: boolean;
  yAxisTitle?: string;
  isPercentageType?: boolean;
  allowedDates?: (date: string) => boolean | undefined;
}

interface IChartSeriesItem {
  name: string;
  data: string[];
}

export default {
  name: 'DashboardGraphs',
  data: (): any => ({
    EChartsTypeKey,
    selectedChart: EChartsTypeKey.USERS,
    datesPeriod: { ...getMonthPeriodDates() },
    loading: false
  }),
  components: {
    VExtendedDataPicker,
    VueApexCharts
  },
  computed: {
    ...mapState('Dashboard', ['NGRData', 'usersStakeData']),
    ...mapGetters('Onboarding', ['operatorId']),
    ...mapGetters('FeatureFlags', ['isEnabledFeatureFlag']),
    disableCharts(): boolean {
      return this.isEnabledFeatureFlag(ELoyaltyFeatureFlag.DASHBOARD_CHARTS);
    },
    selectData(): SelectItem[] {
      return [
        { value: EChartsTypeKey.USERS, text: 'Users' },
        { value: EChartsTypeKey.NGR, text: 'NGR per user' }
      ];
    },
    currentChart(): IChartConfigItem {
      const configs = {
        [EChartsTypeKey.USERS]: {
          key: EChartsTypeKey.USERS,
          data: this.usersStakeData,
          series: this.usersNGRSeries,
          chartType: 'line',
          colors: ['#FF5252', '#696CFF'],
          isShowDatePicker: true,
          isShowDescription: false,
          allowedDates: this.allowedDates
        },
        [EChartsTypeKey.NGR]: {
          key: EChartsTypeKey.NGR,
          data: this.NGRData,
          series: this.ngrSeries,
          chartType: 'bar',
          colors: ['#FF5252', '#696CFF'],
          isShowDatePicker: false,
          isShowDescription: true
        }
      };

      return configs[this.selectedChart] as IChartConfigItem;
    },
    chartOptions(): unknown {
      const data = this.currentChart.data;
      const categories = data.map(
        (item: AvgNgrStatsItem | AvgUsersStatsItem) => item?.date
      );

      return {
        noData: {
          text: this.loading
            ? 'Loading data'
            : 'The data will be available soon',
          style: {
            color: 'rgba(58, 53, 65, 0.3)',
            fontSize: '24px',
            fontFamily: 'Inter'
          }
        },
        stroke: {
          width: 4,
          curve: 'straight'
        },
        markers: {
          size: [5, 6]
        },
        grid: {
          show: !!data.length
        },
        chart: {
          type: this.currentChart.chartType,
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false
          }
        },
        colors: this.currentChart.colors,
        plotOptions: {
          bar: {
            borderRadius: 6
          }
        },
        dataLabels: {
          enabled: false
        },
        legend: {
          position: 'top',
          markers: {
            radius: 0,
            offsetX: -4
          },
          itemMargin: {
            horizontal: 10
          }
        },
        xaxis: {
          categories: categories,
          tooltip: {
            enabled: false
          },
          labels: {
            show: !!data.length,
            rotate: 0,
            style: {
              colors: '#A1ABBF',
              fontSize: '14px'
            }
          },
          axisBorder: {
            show: false
          }
        },
        yaxis: {
          tickAmount: 5,
          axisTicks: {
            color: '#A1ABBF'
          },
          labels: {
            rotate: 0,
            style: {
              colors: '#A1ABBF',
              fontSize: '14px'
            },
            minWidth: 30,
            formatter: (v) => {
              return this.currentChart.isPercentageType
                ? v.toFixed(0) + '%'
                : roundNumber(v, 6, 3);
            }
          },
          title: {
            text: this.currentChart.yAxisTitle,
            style: {
              fontSize: '13px'
            }
          }
        }
      };
    },
    ngrSeries(): IChartSeriesItem[] {
      return [
        this.getSeries(this.NGRData, 'nonStakeNgr', 'Users W/O Stakes'),
        this.getSeries(this.NGRData, 'stakeNgr', 'Users With Stakes')
      ];
    },
    usersNGRSeries(): IChartSeriesItem[] {
      return [
        this.getSeries(
          this.usersStakeData,
          'nonStakeUsersCount',
          'Users W/O Stakes'
        ),
        this.getSeries(
          this.usersStakeData,
          'stakeUsersCount',
          'Users With Stakes'
        )
      ];
    },
    series(): unknown[] {
      return this.loading ? [] : [this.usersNGRSeries, this.ngrSeries];
    },

    minDate(): string {
      return DateTime.utc().minus({ year: 1 }).toFormat('yyyy-LL-dd');
    }
  },
  watch: {
    operatorId: {
      handler(newId: number | null): void {
        if (newId) {
          this.getData();
        }
      },
      immediate: true
    },
    selectedChart: 'getData',
    datesPeriod: 'getData'
  },
  methods: {
    ...mapActions('Dashboard', ['getNGRHistory', 'getUserStakeStats']),
    async getData(): Promise<void> {
      this.loading = true;

      const chartHandlers = {
        [EChartsTypeKey.USERS]: () =>
          this.getUserStakeStats(
            this.getDatesRequestParams(EChartsTypeKey.USERS)
          ),
        [EChartsTypeKey.NGR]: () =>
          this.getNGRHistory(this.getDatesRequestParams(EChartsTypeKey.NGR))
      };

      const handler = chartHandlers[this.selectedChart];
      if (handler) await handler();

      this.loading = false;
    },
    getDatesRequestParams(chartTypeKey: string): ParamsGraphRequest {
      const defaultPeriodChartTypes = [EChartsTypeKey.NGR];

      if (defaultPeriodChartTypes.includes(chartTypeKey)) {
        return { ...getHalfYearPeriodDates() };
      }

      return { ...this.datesPeriod };
    },
    getSeries(
      data: AvgNgrStatsItem[] | AvgUsersStatsItem[],
      valueProp: string,
      name: string
    ): { name: string; data: string[] } {
      return {
        name,
        data: data.map(
          (item: AvgNgrStatsItem | AvgUsersStatsItem) => item[valueProp]
        )
      };
    },
    allowedDates(date: string): boolean {
      return isAllowedPreviousDate(date);
    }
  }
};
